import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Loader2, CheckCircle2, MessageSquare, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DialogHeader } from "@/components/ui/dialog";
import { useAuthStore, logEvent, logException } from "@um/ui";

import { changeUserRole } from "@um/ui";
import { useProfilesStore } from "@/store/useProfilesStore";
import { AddUserForm } from "../AddUserForm";
import { useFirebaseMessagingService } from "@/hooks/useFirebaseMessagingService";
import { useCharactersStore } from "@/store/useCharactersStore";
import { getPersona, type Animal } from "@um/contracts";
import { RichMarkdown } from "@um/ui/components";
import { getUrlWithAttribution } from "@um/ui/utm";
import { sendConversionEvent } from "@/services/social";

interface NotificationsAppIntentBodyProps {
  setHideDialog?: (open: boolean) => void;
  character?: {
    name?: string;
    avatarUrl?: string;
  };
  lite?: boolean;
}

export const NotificationsAppIntentBody: React.FC<
  NotificationsAppIntentBodyProps
> = ({ setHideDialog, character, lite = true }) => {
  const isNative = Capacitor.isNativePlatform();
  const { account, userData, settings, updateSettings, userMind } =
    useAuthStore();
  const nativeAppUrl = getUrlWithAttribution(
    "appNative",
    undefined,
    userData?.conversionData
  );
  const [step, setStep] = React.useState<1 | 2>(1);
  const [addUser, setAddUser] = React.useState(false);
  const [createSelfProfileLoading, setCreateSelfProfileLoading] =
    React.useState(false);
  const [showLiteInput, setShowLiteInput] = React.useState(false);
  const [liteName, setLiteName] = React.useState("");
  const { createProfile, findAdoptableSelfMindId } = useProfilesStore();

  const { getLastCreatedCharacter } = useCharactersStore();
  const [displayCharacter, setDisplayCharacter] = useState(character);
  const [notificationsMessages, setNotificationsMessages] = useState<{
    message?: string;
    image?: string;
    confirmationMessage?: string;
  }>({});

  const fetchLastCreatedCharacter = async () => {
    if (!character && account?.ref?.id) {
      const lastCreatedCharacter = await getLastCreatedCharacter(
        account.ref.id
      );

      if (lastCreatedCharacter) {
        // Get persona from roles, similar to AddAnimalWithJobPage
        const animalCharacter = lastCreatedCharacter as Animal;
        const [personaId, roleData] =
          Object.entries(animalCharacter.roles || {})[0] || [];
        const persona = getPersona(animalCharacter, personaId);

        setDisplayCharacter({
          name: persona?.name,
          avatarUrl: persona?.image?.small || persona?.image?.original,
        });

        // Extract notifications messages from role
        if (roleData?.notifications) {
          setNotificationsMessages({
            image: roleData.notifications.image?.large,
            message: roleData.notifications.message,
            confirmationMessage: roleData.notifications.confirmationMessage,
          });
        }
      }
    }
  };

  useEffect(() => {
    fetchLastCreatedCharacter();
  }, [
    character,
    account?.ref?.id,
    settings.createdDefaultCharacter,
    getLastCreatedCharacter,
  ]);

  const {
    requestPermissionAndSaveToken,
    isLoading: isSavingToken,
    error: errorFcm,
    success: successFcm,
  } = useFirebaseMessagingService();

  useEffect(() => {
    if (settings.acknowledgedNotifications) {
      setStep(2);
    }
  }, [settings.acknowledgedNotifications]);

  const onEnableNotifications = async () => {
    await requestPermissionAndSaveToken(userData!.ref.id);
  };

  const onCreateSelfProfile = async () => {
    setCreateSelfProfileLoading(true);
    try {
      if (userMind) {
        console.log(
          "The user already has a mind, so skipping profile creation"
        );
      } else {
        // `userMind` is derived ONLY from `userRole.mind`, so it reads as null
        // whenever that link is missing — even for an account that already has
        // a Mind. Web onboarding hit exactly that case: the Mind existed, the
        // role link had been cleared, and this dialog (re-shown on the freshly
        // installed app because `acknowledgedNotifications` is a per-DEVICE
        // setting) created a second Mind named after the account (UM-289).
        // Adopt an existing unclaimed Mind instead of blindly creating one.
        const adoptableMindId = await findAdoptableSelfMindId(account?.ref.id);
        if (adoptableMindId) {
          console.log(
            "[NotificationsAppIntent] Adopting the account's existing Mind instead of creating a duplicate:",
            adoptableMindId
          );
        }

        const mindId =
          adoptableMindId ?? (await createProfile({ name: account?.name }));

        // This call can be slow if the function is not warm.
        //
        // `mindId` must be passed: changeUserRole's "update" assigns
        // `userRole.mind = mind?.ref` unconditionally, so omitting it CLEARS
        // the link rather than leaving it untouched — which is what left
        // `userMind` null and caused the duplicate above.
        await changeUserRole({
          action: "update",
          accountId: account!.ref.id,
          inviteEmail: account?.email || "",
          role: "owner",
          mindId,
        });
      }
    } catch (err) {
      // Never leave the dialog spinning. changeUserRole can legitimately
      // reject (e.g. `checkMindConflict` when the Mind is already linked to
      // another role), and before this guard a throw here skipped the
      // acknowledgement below, so the prompt re-fired on every launch.
      logException(err);
      console.error(
        "[NotificationsAppIntent] Failed to set up the self profile:",
        err
      );
    } finally {
      setCreateSelfProfileLoading(false);
    }
    await updateSettings({
      acknowledgedSupportingPerson: true,
    });
    setHideDialog?.(true);
  };

  const onCreateLiteProfile = async () => {
    if (!liteName.trim()) return;

    setCreateSelfProfileLoading(true);
    await createProfile({
      name: liteName.trim(),
    });
    setCreateSelfProfileLoading(false);
    await updateSettings({
      acknowledgedSupportingPerson: true,
    });
    setHideDialog?.(true);
  };

  if (successFcm && step === 1) {
    return (
      <div>
        <div className="flex flex-col items-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-3 flex-col gap-2">
            <CheckCircle2
              className="h-10 w-10 text-green-600"
              aria-hidden="true"
            />
          </div>
          <div className="text-xl">Notifications Enabled!</div>
          <div className="mt-2 text-sm">{successFcm}</div>
        </div>
        <div className="mt-4 sm:justify-center flex justify-center">
          <Button
            onClick={async () => {
              setHideDialog?.(false);
              await updateSettings({
                acknowledgedNotifications: true,
              });
              setStep(2);
            }}
          >
            Done
          </Button>
        </div>
      </div>
    );
  }

  if (addUser && !lite) {
    return (
      <AddUserForm
        isOpen={addUser}
        onOpenChange={setAddUser}
        onSuccess={async () => {
          await updateSettings({
            acknowledgedSupportingPerson: true,
          });
          setHideDialog?.(true);
        }}
      />
    );
  }

  return (
    <div className="p-4">
      <DialogHeader className="text-center">
        <div className="flex flex-col items-center">
          <div className="relative mb-4">
            {/* {step === 1 && ()} */}

            {/* <Avatar className="w-24 h-24 border-4 border-white shadow-lg  ">
              <AvatarImage
                className="object-cover"
                src={displayCharacter?.avatarUrl || ""}
                alt={displayCharacter?.name || "Character"}
              />
              <AvatarFallback className="bg-gray-200">
                <ImageIcon
                  className="w-12 h-12 text-gray-400"
                  aria-hidden="true"
                />
              </AvatarFallback>
            </Avatar> */}
            <div className="absolute -bottom-2 -right-2 bg-purple-500 rounded-full p-2 border-4 border-white">
              <MessageSquare size={20} className="text-white" />
            </div>

            <img
              src={notificationsMessages.image || displayCharacter?.avatarUrl}
              alt={displayCharacter?.name || "Character"}
              className="w-50 h-50 rounded-md shadow-md"
            />
          </div>
          {step === 1 ? (
            <>
              <div className="text-xl font-bold mb-2">
                Enable Character Notifications
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-4 text-left">
                  {notificationsMessages.message ? (
                    <RichMarkdown>{notificationsMessages.message}</RichMarkdown>
                  ) : (
                    <>
                      <p>By enabling notifications, your characters can:</p>
                      <ul className="list-disc list-inside text-left mt-2 space-y-1">
                        <li>Send your daily boosts when they're ready</li>
                        <li>
                          Remind you when it's time to take your medication
                        </li>
                        <li>Let you know when a new story chapter drops</li>
                        <li>Check in with other support you set up</li>
                      </ul>
                      <p className="mt-3">
                        If you skip this, messages will still be here in the
                        app, but your characters can't nudge you at the right
                        time.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-xl font-bold mb-2">
                Who Will Your Characters Support?
              </div>
              <div className="text-sm text-gray-700 mb-2 leading-relaxed text-center max-w-md">
                {notificationsMessages.confirmationMessage ? (
                  <p>{notificationsMessages.confirmationMessage}</p>
                ) : (
                  <>
                    <p>
                      Your characters are designed to be personal supporters —
                      always there to encourage, guide, and connect.
                    </p>
                    <p className="mt-2">
                      Now tell us who they're here to support.
                    </p>
                    <p className="mt-3 font-semibold text-purple-800">
                      Who will your characters be supporting?
                    </p>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </DialogHeader>
      <div className="w-full mt-4 flex flex-col items-center">
        {step === 1 ? (
          <>
            {isSavingToken && (
              <div className="flex items-center justify-center mb-3">
                <Loader2 className="h-5 w-5 animate-spin text-purple-600 mr-2" />
                <p className="text-sm text-purple-600">
                  Enabling notifications...
                </p>
              </div>
            )}

            {errorFcm && !isSavingToken && (
              <p className="text-xs text-red-500 mb-3 p-2 bg-red-50 rounded-md">
                {errorFcm}
              </p>
            )}

            {isNative ? (
              <Button
                onClick={onEnableNotifications}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                disabled={isSavingToken}
              >
                {isSavingToken ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Enable Notifications
              </Button>
            ) : (
              <div className="w-full text-center p-3 bg-gray-100 rounded-lg">
                <p className="text-sm mb-2">
                  To get the full experience, please install our mobile app:
                </p>
                <div className="flex justify-center">
                  <a
                    href={nativeAppUrl}
                    className="inline-block w-full max-w-[280px]"
                    aria-label="Get the app on the App Store or Google Play"
                    onClick={() => sendConversionEvent("app_download_clicked")}
                  >
                    <img
                      src="/assets/combined-download.webp"
                      alt="Download on the App Store and Google Play"
                      className="w-full h-auto object-contain"
                    />
                  </a>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500 text-center mt-3">
              You can turn off notifications anytime in your device settings.
            </p>
          </>
        ) : (
          <div className="w-full flex flex-col space-y-3">
            <Button
              disabled={createSelfProfileLoading}
              onClick={onCreateSelfProfile}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              size="lg"
            >
              {createSelfProfileLoading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Me
            </Button>
            <Button
              disabled={createSelfProfileLoading}
              onClick={() => {
                if (lite) {
                  setShowLiteInput(true);
                } else {
                  setAddUser(true);
                }
              }}
              className="w-full"
              size="lg"
              variant="outline"
            >
              {createSelfProfileLoading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Someone I support (e.g. my child)
            </Button>
          </div>
        )}

        {lite && step === 2 && showLiteInput && (
          <div className="w-full mt-4 space-y-3">
            <div className="text-sm font-medium text-center">
              What's their name?
            </div>
            <Input
              type="text"
              value={liteName}
              onChange={(e) => setLiteName(e.target.value)}
              placeholder="Enter name"
              disabled={createSelfProfileLoading}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && liteName.trim()) {
                  onCreateLiteProfile();
                }
              }}
            />
            <Button
              disabled={createSelfProfileLoading || !liteName.trim()}
              onClick={onCreateLiteProfile}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              size="lg"
            >
              {createSelfProfileLoading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Profile
            </Button>
          </div>
        )}

        {(!lite || step === 1) && (
          <Button
            className="m-4"
            variant="ghost"
            onClick={async () => {
              if (step === 1) {
                await updateSettings({
                  acknowledgedNotifications: true,
                });
                if (settings.acknowledgedSupportingPerson) {
                  setStep(2);
                } else {
                  setHideDialog?.(false);
                }
              } else {
                await updateSettings({
                  acknowledgedSupportingPerson: true,
                });
                setHideDialog?.(true);
              }
            }}
          >
            {step === 1 ? "Not now" : "Skip"}
          </Button>
        )}
      </div>
    </div>
  );
};
